import { logger } from '@/utils/logger';

import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useUserStatusCheck = () => {
  const { user, session, signOut } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!user) return;
      const organizationId = session?.user?.app_metadata?.active_organization_id;
      if (!organizationId) return;

      try {
        // Check active organization membership status
        const { data: membership, error } = await supabase
          .from('organization_members' as any)
          .select('status')
          .eq('user_id', user.id)
          .eq('organization_id', organizationId)
          .maybeSingle() as { data: { status: string } | null; error: any };

        if (error) {
          logger.error('Error checking user status:', error);
          return;
        }

        if (!membership || membership.status === 'Disabled') {
          logger.debug('User membership inactive, signing out...');
          
          // Sign out the user
          await signOut();
          
          // Show a message explaining why they were logged out
          toast({
            title: "Account Disabled",
            description: "Your organization access has been disabled. Please contact support for assistance.",
            variant: "destructive",
            duration: 10000, // Show for 10 seconds
          });
        }
      } catch (error) {
        logger.error('Error in user status check:', error);
      }
    };

    // Check status when user changes
    checkUserStatus();

    // Set up real-time listener for active organization membership changes
    if (user && session?.user?.app_metadata?.active_organization_id) {
      const organizationId = session.user.app_metadata.active_organization_id;
      const channel = supabase
        .channel(`user-status-changes-${organizationId}-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'organization_members',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            logger.debug('Organization membership updated:', payload);
            const newRow = payload.new as { organization_id?: string; status?: string } | null;
            const oldRow = payload.old as { organization_id?: string } | null;
            if (payload.eventType === 'DELETE' && oldRow?.organization_id === organizationId) {
              checkUserStatus();
            } else if (newRow?.organization_id === organizationId && newRow?.status === 'Disabled') {
              checkUserStatus();
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, session, signOut, toast]);
};
